import type React from 'react';
import type { FC, ReactNode } from 'react';
import { INPUT_BASE, INPUT_PADDING, INPUT_STATE } from './inputStyles';

interface InputProps {
  type?: 'text' | 'number' | 'email' | 'password' | 'date' | 'time' | string;
  id?: string;
  name?: string;
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  // Accessible name for inputs with no visible <Label>, e.g. inline table editors.
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  className?: string;
  min?: string;
  max?: string;
  step?: number;
  disabled?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  success?: boolean;
  error?: boolean;
  hint?: string;
  hintId?: string;
  children?: ReactNode;
  ref?: React.Ref<HTMLInputElement>;
}

export const Input: FC<InputProps> = ({
  type = 'text',
  id,
  name,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  inputMode,
  className = '',
  min,
  max,
  step,
  disabled = false,
  readOnly = false,
  autoComplete,
  autoFocus = false,
  success = false,
  error = false,
  hint,
  hintId,
  children,
  ref,
}) => {
  const stateClasses = disabled
    ? INPUT_STATE.disabled
    : readOnly
      ? INPUT_STATE.readOnly
      : error
        ? INPUT_STATE.error
        : success
          ? INPUT_STATE.success
          : INPUT_STATE.default;

  // Concatenated, not twMerge'd: merging would let state and caller classes win
  // conflicts they currently lose, changing how existing inputs render.
  const inputClasses = ` ${INPUT_BASE} ${INPUT_PADDING} ${className} ${stateClasses}`;

  return (
    <>
      <div className="relative">
        <input
          ref={ref}
          type={type}
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          inputMode={inputMode}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={inputClasses}
        />
        {children}
      </div>
      {hint && (
        <p
          id={hintId}
          className={`mt-1.5 text-xs ${error ? 'text-mp-error' : success ? 'text-mp-success' : 'text-mp-muted'
            }`}
        >
          {hint}
        </p>
      )}
    </>
  );
};
